import { useMutation, useQuery, UseQueryResult } from '@tanstack/react-query';
import { AxiosError } from 'axios';

import { axiosInstance } from '../../axios';
import { responseHandler } from '../utils';
import { Envelope } from './types';

/**
 * Regulamin płatności online — publikacja przez HotelSpot.
 *
 * TEN DOKUMENT JEST PODSTAWĄ NASZEJ PROWIZJI i przerzucenia zwrotów oraz sporów na hotel,
 * więc nie może żyć w kodzie frontu: nową treść wgrywa dział prawny bez wdrożenia, a każda
 * opublikowana wersja zostaje w bazie na zawsze, bo przy sporze trzeba pokazać dokładnie to
 * brzmienie, które hotel widział, klikając zgodę.
 *
 * Podbicie wersji odnawia zgody wszystkich hoteli: panel porówna zapisaną wersję
 * z obowiązującą i poprosi o akceptację ponownie.
 */

export const STRIPE_TERMS_VERSIONS_QUERY_KEY = 'stripe-terms-versions';
export const STRIPE_TERMS_VERSION_QUERY_KEY = 'stripe-terms-version';

export interface TermsVersionRow {
  version: string;
  note: string | null;
  /** Znacznik Firestore po serializacji: sekundy epoch. */
  publishedAt: { _seconds?: number; seconds?: number } | null;
  publishedBy: string | null;
  sectionsCount: number;
}

export interface TermsOverview {
  /** Wersja wbudowana we front panelu — obowiązuje, dopóki nic nie opublikowano. */
  builtInVersion: string;
  currentVersion: string | null;
  versions: TermsVersionRow[];
}

export interface TermsVersionDetails {
  version: string;
  note: string | null;
  publishedBy: string | null;
  /** Treść w formacie edycji: blok = sekcja, pierwsza linia bloku to tytuł. */
  text: string;
}

export const useGetStripeTermsVersions = (): UseQueryResult<TermsOverview, AxiosError> => useQuery<
  TermsOverview,
  AxiosError
>({
  queryKey: [STRIPE_TERMS_VERSIONS_QUERY_KEY],
  retry: 0,
  queryFn: async () => {
    const response = await axiosInstance.get<Envelope<TermsOverview>>('/v3-getStripeTermsVersions');
    return responseHandler(response).data;
  },
});

/** Treść jednej wersji — do podglądu i do zaczęcia nowej od poprzedniej. */
export const useGetStripeTermsVersion = (
  version: string | null,
): UseQueryResult<TermsVersionDetails, AxiosError> => useQuery<TermsVersionDetails, AxiosError>({
  queryKey: [STRIPE_TERMS_VERSION_QUERY_KEY, version],
  enabled: !!version,
  retry: 0,
  queryFn: async () => {
    const response = await axiosInstance.get<Envelope<TermsVersionDetails>>('/v3-getStripeTermsVersions', {
      params: { version },
    });
    return responseHandler(response).data;
  },
});

export const usePublishStripeTerms = () => useMutation({
  mutationFn: async (params: { version: string; text: string; note?: string }) => {
    const response = await axiosInstance.post<Envelope<{ version: string }>>('/v3-publishStripeTerms', params);
    return responseHandler(response).data;
  },
});
